import type { CalendarDate } from './Calendar.types'

function pad(value: number): string {
  return `${value}`.padStart(2, '0')
}

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export function isCalendarDate(value: string): value is CalendarDate {
  if (!CALENDAR_DATE_PATTERN.test(value)) return false
  const parsed = parseCalendarDate(value)
  return toCalendarDate(parsed) === value
}

export function toCalendarDate(date: Date): CalendarDate {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Local midnight for the given calendar date. Built field by field rather
 * than parsed, because `new Date('2026-08-19')` is UTC midnight and lands on
 * the previous day everywhere west of Greenwich.
 */
export function parseCalendarDate(date: CalendarDate): Date {
  const year = Number(date.slice(0, 4))
  const month = Number(date.slice(5, 7)) - 1
  const day = Number(date.slice(8, 10))
  return new Date(year, month, day)
}

export function makeCalendarDate(year: number, month: number, day: number): CalendarDate {
  return toCalendarDate(new Date(year, month, day))
}

export function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  const parsed = parseCalendarDate(date)
  parsed.setDate(parsed.getDate() + days)
  return toCalendarDate(parsed)
}

export function calendarDaysBetween(from: CalendarDate, to: CalendarDate): number {
  const start = parseCalendarDate(from)
  const end = parseCalendarDate(to)
  return Math.round((end.getTime() - start.getTime()) / 86_400_000)
}

export function clampCalendarDate(date: CalendarDate, from: CalendarDate, to: CalendarDate): CalendarDate {
  if (date < from) return from
  if (date > to) return to
  return date
}

/** `YYYY-MM`. */
export function toMonthKey(date: CalendarDate): string {
  return date.slice(0, 7)
}

export function makeMonthKey(year: number, month: number): string {
  const normalised = new Date(year, month, 1)
  return `${normalised.getFullYear()}-${pad(normalised.getMonth() + 1)}`
}
